import {
  Archive,
  ArrowUpRight,
  BookOpen,
  Bot,
  Building2,
  Check,
  Code2,
  Copy,
  FolderGit2,
  GitPullRequest,
  Globe,
  HelpCircle,
  Layers,
  Lightbulb,
  type LucideIcon,
  Palette,
  Plus,
  Rocket,
  Server,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Terminal,
  XIcon,
  Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import imgProjectDescription from "./assets/project_description.webp";
import imgStep01 from "./assets/step01_describe.webp";
import imgStep02 from "./assets/step02_review.webp";
import imgStep03 from "./assets/step03_scaffold.webp";
import imgStep04 from "./assets/step04_ready.webp";
import imgStep05Home from "./assets/step05_result_home.webp";
import imgStep05Login from "./assets/step05_result_login.webp";

/* ------------------------------------------------------------------ */
/*  Shared components                                                  */
/* ------------------------------------------------------------------ */

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="text-muted-foreground hover:text-foreground absolute top-2 right-2 rounded p-1 transition-colors"
      title="Copy to clipboard"
    >
      {copied ? <Check className="size-3.5 text-green-500" /> : <Copy className="size-3.5" />}
    </button>
  );
}

function CodeBlock({ code }: { code: string }) {
  return (
    <div className="relative">
      <CopyButton text={code} />
      <pre className="bg-primary/5 overflow-x-auto rounded-md p-4 text-sm">
        <code>{code}</code>
      </pre>
    </div>
  );
}

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-4">
      <div className="bg-accent/10 text-accent flex size-8 shrink-0 items-center justify-center rounded-full text-sm font-bold">
        {number}
      </div>
      <div className="flex-1 pt-0.5">
        <h4 className="text-primary mb-1 font-bold">{title}</h4>
        <div className="text-muted-foreground text-sm">{children}</div>
      </div>
    </div>
  );
}

function Tip({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-accent/5 border-accent/30 rounded-md border-l-4 px-4 py-3">
      <div className="flex items-start gap-2">
        <Lightbulb className="text-accent mt-0.5 size-4 shrink-0" />
        <div className="text-sm">{children}</div>
      </div>
    </div>
  );
}

function ExternalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-accent inline-flex items-center gap-1 underline underline-offset-2 hover:opacity-80"
    >
      {children}
      <ArrowUpRight className="size-3" />
    </a>
  );
}

function BenefitCard({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <div className="border-border rounded-lg border p-4">
      <div className="bg-accent/10 mb-3 inline-flex rounded-lg p-2">
        <Icon className="text-accent size-5" />
      </div>
      <h4 className="text-primary mb-1 font-bold">{title}</h4>
      <p className="text-muted-foreground text-sm">{description}</p>
    </div>
  );
}

function Screenshot({ src, alt }: { src: string; alt: string }) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!expanded) {
      return;
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setExpanded(false);
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [expanded]);

  return (
    <>
      <div
        className="mx-auto max-w-2xl cursor-zoom-in overflow-hidden rounded-lg border shadow-sm transition-shadow hover:shadow-md"
        onClick={() => setExpanded(true)}
      >
        <img src={src} alt={alt} width={1127} height={600} className="h-auto w-full" />
      </div>

      {expanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          onClick={() => setExpanded(false)}
        >
          {}
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <img
              src={src}
              alt={alt}
              className="border-border max-h-[90vh] max-w-[95vw] rounded-xl border shadow-2xl"
            />
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="bg-card text-muted hover:bg-secondary absolute top-2 right-2 rounded-full p-1.5 shadow-md transition-colors"
            >
              <XIcon className="size-4" />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Section type & data                                                */
/* ------------------------------------------------------------------ */

export interface SearchItem {
  title: string;
  keywords: string[];
}

export interface HowToSection {
  id: string;
  title: string;
  icon: LucideIcon;
  description: string;
  content: React.ReactNode;
  searchItems: SearchItem[];
}

export const SECTIONS: HowToSection[] = [
  /* ---- 1. What is Scaffold UI? ---- */
  {
    id: "overview",
    title: "What is Scaffold UI?",
    icon: Sparkles,
    description: "Learn what this app does and the benefits it provides",
    content: (
      <div className="space-y-6">
        <div>
          <h3 className="text-primary mb-2 text-xl font-bold">Your project launchpad</h3>
          <p className="text-muted-foreground">
            Scaffold UI lets you spin up a fully configured, production-ready web application in
            minutes — no coding experience needed. Describe your project, and we handle the rest.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <BenefitCard
            icon={FolderGit2}
            title="Automatic GitHub repo"
            description="A new BOI-internal repository is created from our own vibe-coding template with best-practice project structure, agentic instructions, and tooling baked in."
          />
          <BenefitCard
            icon={Server}
            title="Supabase & Railway setup"
            description="Database, auth, and deployment infrastructure are provisioned and configured automatically, including environment variables."
          />
          <BenefitCard
            icon={Palette}
            title="AI brand personalization"
            description="Optionally personalize the UI with your client's brand colors, logo, and copy using AI, powered by a simple description."
          />
          <BenefitCard
            icon={Zap}
            title="Instant deploy"
            description="Your app goes live on Railway as soon as the scaffold completes, with a domain specific to your client, so you can share it immediately!"
          />
        </div>

        <Tip>
          Scaffold UI is built for Board of Innovation colleagues. If you have questions or run into
          issues, reach out on the{" "}
          <ExternalLink href="https://boardofinnovation.slack.com/archives/C0AMVLWHQDD">
            #boi-scaffold-ui
          </ExternalLink>{" "}
          Slack channel or DM Ruben.
        </Tip>
      </div>
    ),
    searchItems: [
      { title: "What is Scaffold UI?", keywords: ["overview", "about", "introduction", "what"] },
      { title: "Benefits and features", keywords: ["benefit", "feature", "why", "advantage"] },
      {
        title: "Automatic GitHub repository",
        keywords: ["github", "repo", "repository", "template"],
      },
      {
        title: "Supabase & Railway setup",
        keywords: ["supabase", "railway", "database", "deploy"],
      },
      {
        title: "AI brand personalization",
        keywords: ["brand", "personalize", "colors", "logo", "ai"],
      },
    ],
  },

  /* ---- 2. Create a New Project ---- */
  {
    id: "create-project",
    title: "Create a New Project",
    icon: Plus,
    description: "Step-by-step guide to creating your first project",
    content: (
      <div className="space-y-6">
        <p className="text-muted-foreground">
          Creating a project is a simple process. The whole thing takes about 3-5 minutes.
        </p>

        <div className="space-y-5">
          <Step number={1} title="Describe your project">
            <p>
              Click <strong>New Project</strong> and describe what you want to build. Optionally
              pick the <strong>Client</strong> this project belongs to — if it isn&apos;t in the
              list yet, follow the &ldquo;Need a new client?&rdquo; link to create one on the{" "}
              <strong>Clients</strong> page, then come back and your description will still be
              there. Our AI will generate a project name, slug, and description for you — or you can
              skip and fill them in manually.
            </p>
          </Step>

          <Screenshot src={imgStep01} alt="Step 1: Describe your project" />

          <Step number={2} title="Review & configure">
            <div className="space-y-2">
              <p>Review the generated details and tweak anything you like:</p>
              <ul className="list-disc space-y-2 pl-5">
                <li>
                  <strong>Client</strong> — the client picked in the previous step carries over
                  here, pre-filled with its brand. You can still change it to another existing
                  client or leave it <strong>Unassigned</strong>. To add a brand-new client, use the{" "}
                  <strong>Clients</strong> page.
                </li>
                <li>
                  <strong>Project name & slug</strong> — used for the GitHub repo name
                </li>
                <li>
                  <strong>Personalize project</strong> — AI customizes the UI for your client&apos;s
                  brand
                </li>
                <li>
                  <strong>Include Supabase</strong> — adds auth and a Postgres database
                  <ul className="text-muted-foreground mt-1 list-disc space-y-1 pl-5">
                    <li>
                      <strong>Demo credentials</strong> — email and password for the demo account
                    </li>
                    <li>
                      <strong>Allowed email domains</strong> — restrict login to specific domains
                    </li>
                    <li>
                      <strong>Email sender</strong> — the name your project's automated emails
                      (sign-up, password reset) are sent from, e.g.{" "}
                      <code>acme@mail.boardofinnovation.ai</code>
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>Include Railway</strong> — deploys the app to Railway with a public URL
                  <ul className="text-muted-foreground mt-1 list-disc space-y-1 pl-5">
                    <li>
                      <strong>Custom subdomain</strong> — optionally set a subdomain like{" "}
                      <code className="bg-primary/5 rounded px-1 text-xs">
                        your-project.boardofinnovation.com
                      </code>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </Step>

          <Screenshot src={imgStep02} alt="Step 2: Review & configure" />

          <Tip>
            Assigning a <strong>Client</strong> keeps related projects together, and lets you filter
            your projects by client on the home page (and in the admin projects view). Choose it now
            — changing a project&apos;s client afterward is an admin-only action.
          </Tip>

          <Tip>
            The <strong>demo credentials</strong> sign in as an <strong>admin</strong> of your
            deployed app. Scaffold UI suggests a fresh, random password made of three words and a
            number: easy to read out and share with your client, and never based on the project
            name. Anyone who has it gets admin access to the demo, so share it like a secret and
            change it when the people seeing the demo change: from your app&apos;s profile page, or,
            for an app without Supabase (which has no profile page), in its Railway settings. Prefer
            your own password? Just type over the suggestion before you create the project.
          </Tip>

          <Step number={3} title="Watch the magic happen">
            <p>
              Sit back while Scaffold UI creates your GitHub repo, installs dependencies,
              personalizes the UI, sets up Supabase, pushes to GitHub, and deploys to Railway. You
              can see real-time progress and logs for each step.
            </p>
          </Step>

          <Screenshot src={imgStep03} alt="Step 3: Scaffolding in progress" />

          <Tip>
            The scaffolding may take a while, feel free to let it run in the background and come
            back later once it&apos;s done.
          </Tip>

          <Step number={4} title="Success!">
            <p>
              Once the scaffolding completes, Scaffold UI shows you a summary with quick links to
              all your resources — GitHub repo, Supabase dashboard, Railway dashboard, and your live
              app URL. You can also download the{" "}
              <code className="bg-primary/5 rounded px-1 text-xs">.env</code> file right here.
            </p>
          </Step>

          <Screenshot src={imgStep04} alt="Step 4: Project ready in Scaffold UI" />

          <Step number={5} title="You're live!">
            <p>
              Click <strong>Open Your App</strong> to see it in action. You&apos;ll land on the
              login page of your freshly deployed app — log in with the demo credentials you set
              during creation.
            </p>
          </Step>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Screenshot src={imgStep05Login} alt="Your app's login page" />
              <p className="text-muted-foreground text-center text-xs">
                Your app&apos;s login page
              </p>
            </div>
            <div className="space-y-2">
              <Screenshot src={imgStep05Home} alt="Your app's home screen" />
              <p className="text-muted-foreground text-center text-xs">
                Your app&apos;s home screen
              </p>
            </div>
          </div>

          <Tip>
            The scaffolded app is a starting point — it&apos;s intentionally minimal so you can
            build on top of it. The real value comes from what you and your team add next!
          </Tip>
        </div>
      </div>
    ),
    searchItems: [
      { title: "Create a new project", keywords: ["create", "new", "project", "start"] },
      { title: "Describe your project", keywords: ["describe", "prompt", "ai", "generate"] },
      {
        title: "Project configuration options",
        keywords: ["config", "options", "supabase", "railway", "personalize", "domain"],
      },
      {
        title: "Assign a client",
        keywords: [
          "client",
          "clients",
          "group",
          "cluster",
          "filter",
          "assign",
          "unassigned",
          "new client",
          "create client",
          "admin",
          "reassign",
          "change client",
        ],
      },
      {
        title: "Custom subdomain setup",
        keywords: ["subdomain", "domain", "custom", "url", "boardofinnovation"],
      },
      {
        title: "Demo credentials",
        keywords: ["demo", "email", "password", "credentials", "admin", "share", "secret"],
      },
      {
        title: "Email sender",
        keywords: ["email", "sender", "from", "resend", "mail", "notifications"],
      },
      { title: "Allowed email domains", keywords: ["allowed", "email", "domains", "restrict"] },
    ],
  },

  /* ---- 3. Clients ---- */
  {
    id: "clients",
    title: "Clients",
    icon: Building2,
    description: "Browse your clients and manage their brand",
    content: (
      <div className="space-y-6">
        <p className="text-muted-foreground">
          The <strong>Clients</strong> page (in the left sidebar) is where every client your
          projects are grouped under lives &mdash; its name, brand, and the projects tied to it.
        </p>

        <div className="space-y-4">
          <ResourceItem
            icon={Palette}
            title="Set a client's brand"
            description="Click Edit on a client card to set its logo, primary and accent colors, default theme, and a design description. New projects created under that client automatically pick up this brand."
          />
          <ResourceItem
            icon={Sparkles}
            title="Auto-fill a brand from a website"
            description="In the client edit dialog, paste the client's website URL and click Auto-fill from website. It researches the site and fills in the logo, colors, and a design description for you — review the results and adjust anything before saving."
          />
          <ResourceItem
            icon={Layers}
            title="See a client's projects"
            description="Expand a client card to list every project grouped under it, along with its current status."
          />
          <ResourceItem
            icon={Plus}
            title="Create a new client"
            description="Click New Client and give it a name, then set its brand. You can also start this from the New Project dialog — follow the “Need a new client?” link, which brings you right back to your in-progress description afterward."
          />
        </div>

        <Tip>
          The design description isn&apos;t just documentation &mdash; it shapes the look and feel
          of every project scaffolded under that client, so it&apos;s worth reviewing and refining
          after an auto-fill.
        </Tip>

        <Tip>
          Deleting a client only unassigns its projects &mdash; they move back to{" "}
          <strong>Unassigned</strong> and are never deleted themselves. You can group them under a
          client again at any time.
        </Tip>
      </div>
    ),
    searchItems: [
      { title: "Clients page", keywords: ["clients", "client", "manage", "dashboard", "brand"] },
      {
        title: "Set a client's brand",
        keywords: [
          "brand",
          "logo",
          "colors",
          "theme",
          "personalize",
          "edit client",
          "design description",
        ],
      },
      {
        title: "Auto-fill a brand from a website",
        keywords: [
          "auto-fill",
          "autofill",
          "website",
          "url",
          "brand from url",
          "research",
          "ai",
          "design description",
        ],
      },
      {
        title: "See a client's projects",
        keywords: ["client", "projects", "expand", "list"],
      },
      {
        title: "Create a new client",
        keywords: ["new client", "create client", "add client"],
      },
      {
        title: "Delete a client",
        keywords: ["delete", "remove", "client", "unassign", "unassigned"],
      },
    ],
  },

  /* ---- 4. What Gets Created ---- */
  {
    id: "sources",
    title: "What Gets Created",
    icon: Layers,
    description: "Overview of all the resources provisioned for your project",
    content: (
      <div className="space-y-6">
        <p className="text-muted-foreground">
          After your project is scaffolded, you&apos;ll have access to all the resources beow. You
          can find links to each of them on the project detail page.
        </p>

        <div className="space-y-4">
          <ResourceItem
            icon={FolderGit2}
            title="GitHub Repository"
            description="A full-stack monorepo (Next.js frontend + FastAPI backend) created from the BOI vibe template. Includes CI/CD, linting, and tooling pre-configured."
          />
          <ResourceItem
            icon={Bot}
            title="Agentic Setup"
            description="Your repo comes pre-loaded with Claude Code agent instructions, custom agents, and coding guidelines. AI assistants understand your project structure out of the box so you can focus on adding value."
          />
          <ResourceItem
            icon={Server}
            title="Supabase Project"
            description="A Postgres database with authentication, Row Level Security, and API layer. Your .env file is pre-configured with the connection strings."
          />
          <ResourceItem
            icon={ShieldCheck}
            title="Auth & User Management"
            description="A fully working signup/login flow with email verification, an admin user (your demo credentials), and out-of-the-box user management. Restrict access with allowed email domains."
          />
          <ResourceItem
            icon={Rocket}
            title="Railway Deployment"
            description="Your app is deployed and live with a public client-specific URL. The frontend and backend run as separate services connected via private networking."
          />
          <ResourceItem
            icon={Settings}
            title=".env File"
            description="All environment variables (Supabase keys, database URL, etc.) ready to download. You'll need this for local development."
          />
          <ResourceItem
            icon={BookOpen}
            title="Setup Instructions"
            description="AI-generated, project-specific instructions for running the app locally. Available from the project detail page as a downloadable markdown file."
          />
        </div>

        <Screenshot src={imgProjectDescription} alt="Project detail page with resource links" />

        <Tip>
          You can download your <code className="bg-primary/5 rounded px-1 text-xs">.env</code> file
          and setup instructions from the project detail page at any time. You&apos;ll need this to
          get started locally!
        </Tip>
      </div>
    ),
    searchItems: [
      { title: "GitHub repository", keywords: ["github", "repo", "repository", "code"] },
      { title: "Supabase project", keywords: ["supabase", "database", "auth", "postgres"] },
      { title: "Railway deployment", keywords: ["railway", "deploy", "hosting", "live"] },
      { title: "Live app URL", keywords: ["url", "domain", "live", "app", "link"] },
      { title: ".env file", keywords: ["env", "environment", "variables", "keys", "secrets"] },
      { title: "Setup instructions", keywords: ["instructions", "setup", "guide", "readme"] },
    ],
  },

  /* ---- 5. Start Coding Locally ---- */
  {
    id: "local-setup",
    title: "Start Coding Locally",
    icon: Terminal,
    description: "Clone, install, and run the project on your machine",
    content: (
      <div className="space-y-6">
        <Tip>
          Each project comes with its own personalized{" "}
          <strong>&ldquo;How to get started&rdquo;</strong> guide — available from the project
          detail page. It includes your exact repo URL, project-specific commands, and tailored
          setup instructions. Use that for the most accurate steps! Below is a general overview.
        </Tip>

        <Tip>
          Prefer to let Claude do it? On a finished project&apos;s detail page you&apos;ll find{" "}
          <strong>&ldquo;Set up in Claude&rdquo;</strong> with a <strong>Copy prompt</strong>{" "}
          button. Copy it, paste it into Claude, and it clones the repo, installs everything, starts
          the app and tells you which link to open. If your project uses Supabase, click{" "}
          <strong>Download .env</strong> first (see step 2) so Claude can put that file in place.
          The steps below are the manual route if you&apos;d rather do it yourself.
        </Tip>

        <p className="text-muted-foreground">
          General steps to get any scaffolded project running:
        </p>

        <div className="space-y-5">
          <Step number={1} title="Clone the repository">
            <p className="mb-2">
              Copy the GitHub repo URL from your project detail page and clone it:
            </p>
            <CodeBlock
              code={
                "git clone https://github.com/Board-of-Innovation/your-project.git\ncd your-project"
              }
            />
          </Step>

          <Step number={2} title="Download the .env file">
            <p>
              From the project detail page in Scaffold UI, click the <strong>Download .env</strong>{" "}
              button. Save it to the <strong>project root</strong> of your cloned repo (next to the{" "}
              <code className="bg-primary/5 rounded px-1 text-xs">Makefile</code>).
            </p>
          </Step>

          <Step number={3} title="Install dependencies">
            <p className="mb-2">The project uses npm (frontend) and uv (backend):</p>
            <CodeBlock
              code={
                "# Install everything at once\nmake prepare\n\n# Or install separately:\ncd frontend && npm install\ncd backend && uv sync"
              }
            />
          </Step>

          <Step number={4} title="Start the dev servers">
            <p className="mb-2">Run both frontend and backend with a single command:</p>
            <CodeBlock code="make dev" />
          </Step>

          <Step number={5} title="Open in your browser">
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <ExternalLink href="http://localhost:3000">localhost:3000</ExternalLink> — the
                frontend app. Log in with your demo credentials to see it in action.
              </li>
              <li>
                <ExternalLink href="http://localhost:8000">localhost:8000</ExternalLink> — the
                backend API docs (Swagger UI). Useful for exploring and testing API endpoints
                directly.
              </li>
            </ul>
          </Step>
        </div>

        <Tip>
          Your project also has a{" "}
          <code className="bg-primary/5 rounded px-1 text-xs">Makefile</code> with useful commands.
          Run <code className="bg-primary/5 rounded px-1 text-xs">make help</code> to see all
          available options.
        </Tip>
      </div>
    ),
    searchItems: [
      {
        title: "Set up in Claude prompt",
        keywords: ["claude", "prompt", "copy", "setup", "paste", "automatic"],
      },
      { title: "Clone the repository", keywords: ["clone", "git", "download", "repo"] },
      { title: "Download .env file", keywords: ["env", "download", "environment", "variables"] },
      {
        title: "Install dependencies",
        keywords: ["install", "npm", "uv", "dependencies", "setup"],
      },
      { title: "Start dev servers", keywords: ["dev", "start", "run", "server", "local"] },
      { title: "Makefile commands", keywords: ["make", "makefile", "commands", "help"] },
    ],
  },

  /* ---- 6. Advanced Features ---- */
  {
    id: "advanced",
    title: "Advanced Features",
    icon: Code2,
    description: "Personalization, user management, custom domains, and more",
    content: (
      <div className="space-y-6">
        <p className="text-muted-foreground">
          Power-user features that can accelerate your workflow and make your projects shine.
        </p>

        <div className="space-y-4">
          <AdvancedFeature
            icon={ShieldCheck}
            title="User Management"
            description="Every scaffolded app ships with a complete user management system out of the box: sign-up with email verification, password reset, admin dashboard, audit logs, and email domain protection. No extra setup required."
          />

          <AdvancedFeature
            icon={Palette}
            title="Brand Personalization"
            description="When you enable 'Personalize project' during creation, AI analyzes your client's website and applies their brand colors, typography, and messaging throughout the app. This includes the logo, color scheme, headings, and call-to-action copy."
          />

          <AdvancedFeature
            icon={Globe}
            title="Custom Subdomains"
            description="Set a custom subdomain like your-project.boardofinnovation.com during project creation. This is configured automatically via GoDaddy DNS and Cloudflare — no manual setup needed."
          />

          <AdvancedFeature
            icon={Settings}
            title="Allowed Email Domains"
            description="Restrict who can log into your project by specifying allowed email domains (e.g. boardofinnovation.com, client.com). Users outside these domains will be blocked at signup."
          />

          <AdvancedFeature
            icon={Layers}
            title="Project Members"
            description="Add team members to your project from the project detail page. Members can view the project and its logs. Invite non-users via email to get them started."
          />

          <AdvancedFeature
            icon={GitPullRequest}
            title="AI-Powered PR Agents"
            description="Your GitHub repo comes with Claude Code PR agents that can automatically review pull requests, suggest improvements, and help you write better code. They're triggered automatically when you open a PR."
          />

          <AdvancedFeature
            icon={Archive}
            title="Archiving a Project"
            description="When a project is finished with, an admin can archive it so it stops costing money. Its database and its running app are permanently deleted, while its GitHub repository is kept and the project stays listed here, marked as Archived. Everything that was in the database is saved first, and an admin can download that saved copy at any time. Archiving cannot be undone, so an archived project does not come back on its own. To help decide what to archive, the admin projects list shows when each project was last signed into, so the ones nobody is using any more are easy to spot."
          />

          <AdvancedFeature
            icon={ShieldAlert}
            title="Error Monitoring with Sentry"
            description="Once a project starts getting real use, open its detail page and click 'Set up Sentry'. That creates the project's own Sentry projects and, for deployed projects, fills in the keys automatically. Copy the prompt next to the button and paste it into Claude to add the monitoring code to your repo."
          />
        </div>
      </div>
    ),
    searchItems: [
      {
        title: "AI PR agents",
        keywords: ["pr", "agent", "review", "claude", "ai", "pull request"],
      },
      {
        title: "Error monitoring with Sentry",
        keywords: ["sentry", "errors", "monitoring", "crash", "observability", "alerts"],
      },
      {
        title: "Archiving a project",
        keywords: [
          "archive",
          "archived",
          "shut down",
          "delete project",
          "restore",
          "dump",
          "cost",
          "last login",
          "last sign in",
          "unused",
        ],
      },
      {
        title: "Brand personalization",
        keywords: ["brand", "personalize", "colors", "logo", "customize"],
      },
      {
        title: "Custom subdomains",
        keywords: ["subdomain", "domain", "dns", "godaddy", "cloudflare"],
      },
      {
        title: "User management",
        keywords: ["user", "signup", "password", "reset", "audit", "admin", "management"],
      },
      { title: "Allowed email domains", keywords: ["email", "domains", "restrict", "access"] },
      { title: "Project members", keywords: ["members", "team", "invite", "collaborate"] },
    ],
  },

  /* ---- 7. FAQs ---- */
  {
    id: "faqs",
    title: "FAQs",
    icon: HelpCircle,
    description: "Common questions, mistakes, and troubleshooting tips",
    content: (
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Answers to the most common questions. This section is updated regularly.
        </p>

        <Accordion type="multiple" defaultValue={["faq-1"]} className="w-full">
          <AccordionItem value="faq-1">
            <AccordionTrigger>My project creation failed — what do I do?</AccordionTrigger>
            <AccordionContent>
              <div className="text-muted-foreground space-y-2">
                <p>
                  Check the error message on the project card. Common causes include GitHub API rate
                  limits and Railway deployment timeouts. You can try creating the project again.
                </p>
                <p>
                  If the error persists, check the project logs (click the project card, then the
                  &ldquo;Logs&rdquo; tab) for more details, and reach out on{" "}
                  <ExternalLink href="https://boardofinnovation.slack.com/archives/C0AMVLWHQDD">
                    #boi-scaffold-ui
                  </ExternalLink>{" "}
                  or DM Ruben.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="faq-2">
            <AccordionTrigger>How do I connect my GitHub account?</AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground">
                On the home page, click &ldquo;Connect GitHub&rdquo; in the green banner at the top.
                You&apos;ll be redirected to GitHub to authorize the Scaffold UI app. Once
                authorized, you&apos;ll be redirected back and can start creating projects.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="faq-3">
            <AccordionTrigger>
              I can&apos;t find my .env file — where do I download it?
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground">
                Click on your project card to open the detail view. In the &ldquo;Details&rdquo;
                tab, you&apos;ll see a &ldquo;Download .env&rdquo; button. Save it to the project
                root of your cloned repo.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="faq-4">
            <AccordionTrigger>My local dev server shows errors after cloning</AccordionTrigger>
            <AccordionContent>
              <div className="text-muted-foreground space-y-2">
                <p>Most common issues:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>
                    <strong>Missing .env file</strong> — download it from the project detail page
                    and place it in the project root
                  </li>
                  <li>
                    <strong>Dependencies not installed</strong> — run{" "}
                    <code className="bg-primary/5 rounded px-1 text-xs">make prepare</code>
                  </li>
                  <li>
                    <strong>Wrong Node/Python version</strong> — check the versions in{" "}
                    <code className="bg-primary/5 rounded px-1 text-xs">package.json</code> and{" "}
                    <code className="bg-primary/5 rounded px-1 text-xs">pyproject.toml</code>
                  </li>
                </ul>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="faq-5">
            <AccordionTrigger>Can I change the project name after creation?</AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground">
                The project name and slug are set at creation time and tied to the GitHub repository
                name. If you need a different name, it&apos;s easiest to create a new project.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="faq-6">
            <AccordionTrigger>How do I delete a project?</AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground">
                Open the project detail page and go to the &ldquo;Resources&rdquo; tab. There you
                can delete individual resources (GitHub repo, Supabase, Railway), or use
                &ldquo;Delete all&rdquo; to remove every connected resource in one step instead of
                one at a time. Once nothing is left connected, you can remove the project itself.
                Only project owners and admins can delete projects.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="faq-7">
            <AccordionTrigger>Why doesn&apos;t the backend have authentication?</AccordionTrigger>
            <AccordionContent>
              <div className="text-muted-foreground space-y-2">
                <p>
                  This is by design and completely safe. In production, the backend runs in a{" "}
                  <strong>private subnet on Railway</strong> — it&apos;s never exposed to the
                  outside world. Only the frontend can reach it via Railway&apos;s internal private
                  networking.
                </p>
                <p>
                  The frontend (Next.js) handles all authentication via cookie-based sessions and
                  forwards the user&apos;s identity to the backend through internal headers. Adding
                  JWT or similar auth on the backend would be unnecessary overhead without any
                  security benefit.
                </p>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="faq-8">
            <AccordionTrigger>
              My project&apos;s Supabase and Railway are gone, what happened?
            </AccordionTrigger>
            <AccordionContent>
              <p className="text-muted-foreground">
                It was most likely <strong>archived</strong>. When a project is no longer being
                used, an admin can archive it so it stops costing money: its database and its
                running app are permanently deleted, and the project is marked
                &ldquo;Archived&rdquo; here. Your code is untouched, the GitHub repository is still
                there. Everything that was in the database was saved before anything was deleted, so
                ask an admin and they can download that copy and send it to you. Archiving cannot be
                reversed, so a project that needs to run again has to be set up fresh.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    ),
    searchItems: [
      { title: "Project creation failed", keywords: ["fail", "error", "broken", "timeout"] },
      { title: "Connect GitHub account", keywords: ["github", "connect", "authorize", "login"] },
      { title: "Download .env file", keywords: ["env", "download", "missing", "variables"] },
      { title: "Local dev server errors", keywords: ["error", "local", "dev", "server", "broken"] },
      { title: "Change project name", keywords: ["rename", "change", "name", "slug"] },
      {
        title: "Delete a project",
        keywords: ["delete", "remove", "project", "delete all", "resources", "bulk", "teardown"],
      },
      {
        title: "Backend authentication",
        keywords: ["backend", "auth", "jwt", "security", "private", "subnet"],
      },
      {
        title: "Supabase and Railway are gone",
        keywords: ["archive", "archived", "shut down", "delete project", "restore", "dump", "cost"],
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Helper components used in sections                                 */
/* ------------------------------------------------------------------ */

function ResourceItem({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-4 rounded-lg border p-4">
      <div className="bg-accent/10 flex size-10 shrink-0 items-center justify-center rounded-lg">
        <Icon className="text-accent size-5" />
      </div>
      <div>
        <h4 className="text-primary font-bold">{title}</h4>
        <p className="text-muted-foreground mt-0.5 text-sm">{description}</p>
      </div>
    </div>
  );
}

function AdvancedFeature({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-4 rounded-lg border p-4">
      <div className="bg-accent/10 flex size-10 shrink-0 items-center justify-center rounded-lg">
        <Icon className="text-accent size-5" />
      </div>
      <div>
        <h4 className="text-primary font-bold">{title}</h4>
        <p className="text-muted-foreground mt-0.5 text-sm">{description}</p>
      </div>
    </div>
  );
}
